import { ReactNode } from 'react';
import { useLocation, Link } from 'react-router-dom';
import {
  LayoutDashboard,
  Wallet,
  ArrowLeftRight,
  TrendingUp,
  MoreHorizontal,
} from 'lucide-react';
import { SidebarProvider, SidebarTrigger } from '@/components/ui/sidebar';
import { AppSidebar } from '@/components/AppSidebar';
import { cn } from '@/lib/utils';

const mobileNavItems = [
  { title: 'Dashboard', url: '/', icon: LayoutDashboard },
  { title: 'Accounts', url: '/accounts', icon: Wallet },
  { title: 'Transactions', url: '/transactions', icon: ArrowLeftRight },
  { title: 'Investments', url: '/investments', icon: TrendingUp },
  { title: 'More', url: '/settings', icon: MoreHorizontal },
];

export function AppLayout({ children }: { children: ReactNode }) {
  const { pathname } = useLocation();

  return (
    <SidebarProvider>
      <div className="min-h-screen flex w-full">
        <AppSidebar />
        <div className="flex-1 flex flex-col min-w-0">
          {/* Header */}
          <header className="sticky top-0 z-30 h-14 flex items-center border-b border-border/50 bg-background/80 backdrop-blur-lg px-4 md:px-6">
            <SidebarTrigger className="mr-3 hidden md:flex" />
            <div className="flex items-center gap-2">
              <img src="/favicon.svg" alt="Koin Logo" className="h-5 w-5" />
              <h2 className="text-sm font-semibold tracking-tight text-foreground">
                Koin
              </h2>
            </div>
          </header>

          {/* Main content */}
          <main className="flex-1 overflow-auto p-4 md:p-6 pb-20 md:pb-6 animate-fade-in">
            {children}
          </main>
        </div>
      </div>

      {/* Mobile bottom navigation */}
      <nav className="fixed bottom-0 inset-x-0 z-40 md:hidden border-t border-border/50 bg-background/90 backdrop-blur-lg">
        <div className="flex items-center justify-around h-16 px-2">
          {mobileNavItems.map((item) => {
            const isActive =
              item.url === '/'
                ? pathname === '/'
                : pathname.startsWith(item.url);

            return (
              <Link
                key={item.title}
                to={item.url}
                className={cn(
                  'flex flex-col items-center justify-center gap-1 flex-1 py-1 rounded-lg transition-colors',
                  isActive
                    ? 'text-primary'
                    : 'text-muted-foreground hover:text-foreground',
                )}
              >
                <item.icon className={cn('h-5 w-5', isActive && 'stroke-[2.5]')} />
                <span className="text-[10px] font-medium leading-none">
                  {item.title}
                </span>
              </Link>
            );
          })}
        </div>
      </nav>
    </SidebarProvider>
  );
}
